'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface PendingUploadsContextValue {
  hasPendingUploads: boolean;
  trackUpload: <T>(upload: Promise<T>) => Promise<T>;
}

// Default value keeps consumers working when no provider is mounted:
// uploads are simply not tracked and navigation is never blocked.
const PendingUploadsContext = createContext<PendingUploadsContextValue>({
  hasPendingUploads: false,
  trackUpload: (upload) => upload,
});

/**
 * Tracks in-flight file uploads so navigation controls can block the user
 * from advancing/submitting while a file is still being uploaded to cloud
 * storage. Submitting with an unfinished upload makes the backend fail the
 * whole claim with `not_found_file`.
 */
export const PendingUploadsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [pendingCount, setPendingCount] = useState(0);

  const trackUpload = useCallback(
    async <T,>(upload: Promise<T>): Promise<T> => {
      setPendingCount((count) => count + 1);

      try {
        return await upload;
      } finally {
        setPendingCount((count) => count - 1);
      }
    },
    [],
  );

  const value = useMemo(
    () => ({
      hasPendingUploads: pendingCount > 0,
      trackUpload,
    }),
    [pendingCount, trackUpload],
  );

  return (
    <PendingUploadsContext.Provider value={value}>
      {children}
    </PendingUploadsContext.Provider>
  );
};

export const usePendingUploads = () => useContext(PendingUploadsContext);
